import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Microphone, Stop, Trash, Star, Play, Pause, Upload, Sparkle, Info, CircleNotch, Check } from "@phosphor-icons/react";
import { API, BACKEND_URL } from "@/lib/api";

/**
 * Clone My Voice studio.
 *
 * Three jobs:
 *   1. Record OR upload an audio sample (60s–3min) and clone it via the
 *      user's BYOK ElevenLabs key.
 *   2. List existing cloned voices, set one as the global default, delete
 *      voices that are no longer wanted.
 *   3. AI Voice Director — persistent speech directives (speed slider for
 *      now; ElevenLabs stability/similarity/style sliders when ElevenLabs
 *      is the user's active provider).
 *
 * Provider scope: ElevenLabs ships in this MVP. MiniMax (via muapi) is
 * stubbed as "Coming Soon" — the locked spec calls for it but it requires
 * the muapi voice-cloning playbook which isn't wired yet.
 */

const RECORD_MIN_SEC = 60;
const RECORD_MAX_SEC = 180;
const RECOMMEND_SEC = 120;
// Tighter than ElevenLabs Instant Voice Clone (which accepts up to 5 min)
// to avoid creators waiting too long for a marginally-better clone.

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const CloneMyVoice = () => {
  // ---- Provider state ----
  const [provider, setProvider] = useState("elevenlabs"); // "elevenlabs" | "minimax"
  const [elevenlabsAvailable, setElevenlabsAvailable] = useState(false);
  const [elevenlabsStatus, setElevenlabsStatus] = useState("missing");
  const [muapiAvailable, setMuapiAvailable] = useState(false);

  // ---- Recorder state ----
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // ---- Upload state (alternative to recording) ----
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);

  // ---- Cloning state ----
  const [voiceName, setVoiceName] = useState("");
  const [description, setDescription] = useState("");
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState(null);
  const [cloneSuccess, setCloneSuccess] = useState(null);

  // ---- Library state ----
  const [clonedVoices, setClonedVoices] = useState([]);
  const [previewingId, setPreviewingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ---- AI Voice Director state ----
  const [director, setDirector] = useState({ speed: 1.0, stability: 0.5, similarity_boost: 0.75, style: 0.0 });
  const [savingDirector, setSavingDirector] = useState(false);

  // ---- Key test state ----
  const [testingKey, setTestingKey] = useState(false);
  const [keyTestResult, setKeyTestResult] = useState(null);

  // Mount: check ElevenLabs + muapi availability + load cloned voices + load director
  const loadAll = useCallback(async () => {
    try {
      const [providers, cloned, dir, keysRes] = await Promise.all([
        axios.get(`${API}/voice/providers`),
        axios.get(`${API}/voice/cloned`).catch(() => ({ data: { voices: [] } })),
        axios.get(`${API}/voice/director`).catch(() => ({ data: {} })),
        axios.get(`${API}/keys`).catch(() => ({ data: { keys: {} } })),
      ]);
      const ell = (providers.data?.providers || []).find((p) => p.id === "elevenlabs");
      setElevenlabsAvailable(Boolean(ell?.available));
      // Same resolved status string as Account Settings so every voice
      // surface tells the truth consistently (validated/present_unvalidated/…).
      setElevenlabsStatus(ell?.key_status || (ell?.available ? "validated" : "missing"));
      // muapi key is the gate for MiniMax voice cloning
      const muapiKeys = keysRes.data?.keys?.muapi || {};
      setMuapiAvailable(Boolean(muapiKeys.api_key));
      setClonedVoices(cloned.data?.voices || []);
      if (dir.data) {
        setDirector({
          speed: Number(dir.data.speed ?? 1.0),
          stability: Number(dir.data.stability ?? 0.5),
          similarity_boost: Number(dir.data.similarity_boost ?? 0.75),
          style: Number(dir.data.style ?? 0.0),
        });
      }
    } catch (e) {
      console.warn("CloneMyVoice load failed:", e);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
      if (uploadedUrl) URL.revokeObjectURL(uploadedUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Recorder ----
  const startRecording = async () => {
    setCloneError(null);
    setCloneSuccess(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setRecordedBlob(blob);
        if (recordedUrl) URL.revokeObjectURL(recordedUrl);
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      mr.start();
      setRecording(true);
      setElapsed(0);
      setRecordedBlob(null);
      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          const next = prev + 1;
          if (next >= RECORD_MAX_SEC) {
            stopRecording();
            return RECORD_MAX_SEC;
          }
          return next;
        });
      }, 1000);
    } catch (e) {
      setCloneError("Microphone access denied. Allow mic permissions in your browser and reload.");
    }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const discardRecording = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsed(0);
  };

  // ---- Upload alternative ----
  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("audio/") && !/\.(mp3|wav|m4a|webm|ogg|flac)$/i.test(file.name)) {
      setCloneError("Pick an audio file (mp3, wav, m4a, webm, ogg, or flac).");
      return;
    }
    setUploadedFile(file);
    if (uploadedUrl) URL.revokeObjectURL(uploadedUrl);
    setUploadedUrl(URL.createObjectURL(file));
    setCloneError(null);
  };

  // ---- Cloning ----
  const submitClone = async () => {
    if (!voiceName.trim()) { setCloneError("Give your voice a name (e.g. \"Brittany — gossip wisdom\")."); return; }
    const blob = recordedBlob || uploadedFile;
    if (!blob) { setCloneError("Record or upload an audio sample first."); return; }
    if (recordedBlob && elapsed < RECORD_MIN_SEC) {
      setCloneError(`Need at least ${RECORD_MIN_SEC}s of audio for a usable clone. Keep talking — ${RECORD_MIN_SEC - elapsed}s to go.`);
      return;
    }
    if (provider === "elevenlabs" && !elevenlabsAvailable) {
      setCloneError("ElevenLabs API key required. Add it in Credits & Keys (free tier supports cloning).");
      return;
    }
    if (provider === "minimax" && !muapiAvailable) {
      setCloneError("muapi API key required for MiniMax. Add it in Credits & Keys → muapi.");
      return;
    }
    setCloning(true);
    setCloneError(null);
    setCloneSuccess(null);
    try {
      const fd = new FormData();
      fd.append("voice_name", voiceName.trim());
      fd.append("description", description.trim());
      const filename = recordedBlob ? `clone-${Date.now()}.webm` : uploadedFile.name;
      fd.append("files", blob, filename);
      const endpoint = provider === "minimax"
        ? `${API}/voice/minimax/clone`
        : `${API}/voice/elevenlabs/clone`;
      const res = await axios.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const providerLabel = provider === "minimax" ? "MiniMax" : "ElevenLabs";
      setCloneSuccess(`✓ "${res.data.name}" cloned successfully via ${providerLabel}. It now appears in every voice picker across the app.`);
      // Reset form
      setVoiceName("");
      setDescription("");
      discardRecording();
      setUploadedFile(null);
      if (uploadedUrl) URL.revokeObjectURL(uploadedUrl);
      setUploadedUrl(null);
      await loadAll();
    } catch (e) {
      setCloneError(e.response?.data?.detail || "Cloning failed. Try a longer, clearer audio sample.");
    } finally {
      setCloning(false);
    }
  };

  // ---- Library actions ----
  const setAsDefault = async (voiceId) => {
    try {
      await axios.post(`${API}/voice/cloned/${voiceId}/set-default`);
      await loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not set as default");
    }
  };

  const deleteCloned = async (voiceId) => {
    if (!window.confirm("Delete this cloned voice? This also frees the slot on your ElevenLabs account.")) return;
    setDeletingId(voiceId);
    try {
      await axios.delete(`${API}/voice/cloned/${voiceId}`);
      await loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not delete");
    } finally {
      setDeletingId(null);
    }
  };

  const previewCloned = async (voice) => {
    if (previewingId === voice.voice_id) return;
    setPreviewingId(voice.voice_id);
    try {
      const res = await axios.post(`${API}/voice/generate`, {
        text: "Welcome to The Branding Bar Lab — this is what your cloned voice sounds like.",
        provider: "elevenlabs",
        voice_id: voice.voice_id,
      });
      const url = `${BACKEND_URL}${res.data.audio_url}`;
      const a = new Audio(url);
      a.onended = () => setPreviewingId(null);
      a.play();
    } catch (e) {
      setPreviewingId(null);
      alert(e.response?.data?.detail || "Preview failed");
    }
  };

  // ---- AI Voice Director ----
  const saveDirector = async (next) => {
    setSavingDirector(true);
    try {
      await axios.post(`${API}/voice/director`, next);
      setDirector(next);
    } catch (e) {
      console.warn("Director save failed", e);
    } finally {
      setSavingDirector(false);
    }
  };

  // ---- Test ElevenLabs key ----
  const testKey = async () => {
    setTestingKey(true);
    setKeyTestResult(null);
    try {
      const res = await axios.get(`${API}/voice/elevenlabs/test`);
      setKeyTestResult(res.data);
    } catch (e) {
      setKeyTestResult({ ok: false, code: "network", message: e.response?.data?.detail || "Network error" });
    } finally {
      setTestingKey(false);
    }
  };

  // ---- UI helpers ----
  const blob = recordedBlob || uploadedFile;
  const durationOk = recordedBlob ? elapsed >= RECORD_MIN_SEC : Boolean(uploadedFile);
  const providerReady = provider === "elevenlabs" ? elevenlabsAvailable : muapiAvailable;
  const durationColor = elapsed < RECORD_MIN_SEC
    ? "text-red-500"
    : elapsed <= RECORD_MAX_SEC
      ? "text-emerald-600"
      : "text-amber-500";

  return (
    <div className="space-y-6" data-testid="clone-my-voice-section">
      <div>
        <h2 className="text-xl font-bold text-zinc-900">Clone My Voice</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Record or upload 60 seconds of clean speech. We clone your voice via ElevenLabs and surface it across Script Studio, Auto Create, Bulk Create, Series Studio, and Long-Form Studio.
        </p>
      </div>

      {/* BYOK warning + test connection */}
      {provider === "elevenlabs" && !elevenlabsAvailable ? (
        <div className="card p-4 border-amber-200 bg-amber-50/40" data-testid="elevenlabs-byok-warning">
          <div className="flex items-start gap-3">
            <Info size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-1">{elevenlabsStatus === "missing" ? "Connect ElevenLabs to start cloning" : elevenlabsStatus === "invalid_permissions" ? "Your ElevenLabs key needs more permissions" : elevenlabsStatus === "quota_or_plan_issue" ? "Your ElevenLabs quota/plan needs attention" : "Your ElevenLabs key couldn't be validated"}</p>
              <p className="text-amber-800">
                Sign up free at <a href="https://elevenlabs.io/app/sign-up" target="_blank" rel="noopener noreferrer" className="underline font-semibold">elevenlabs.io</a>{" "}
                (no card required), grab your key from <span className="font-mono text-xs">Settings → API Keys</span>, and paste it in{" "}
                <a href="#" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="nav-account"]')?.click(); }} className="underline font-semibold">Credits &amp; Keys → ElevenLabs</a>.
              </p>
              <p className="text-amber-800 mt-2 text-xs leading-relaxed">
                ⚠ <strong>Important:</strong> When creating the key, set permissions to <strong>&quot;Has Access&quot; for ALL scopes</strong> (especially Voices &amp; Text to Speech). The default &quot;Restricted&quot; preset will get rejected with a 401.
              </p>
            </div>
          </div>
        </div>
      ) : provider === "minimax" && !muapiAvailable ? (
        <div className="card p-4 border-amber-200 bg-amber-50/40" data-testid="minimax-byok-warning">
          <div className="flex items-start gap-3">
            <Info size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-1">Add your muapi key to use MiniMax</p>
              <p className="text-amber-800">
                MiniMax voice cloning runs through muapi.ai (you already use this key for Veo 3.1 and image generation). Paste your muapi key in{" "}
                <a href="#" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="nav-account"]')?.click(); }} className="underline font-semibold">Credits &amp; Keys → muapi</a>.
              </p>
            </div>
          </div>
        </div>
      ) : provider === "elevenlabs" ? (
        <div className="card p-4" data-testid="elevenlabs-test-card">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                <Check size={14} className="text-emerald-500" />
                ElevenLabs key saved
              </p>
              <p className="text-xs text-zinc-500 mt-0.5">
                Run a quick health check to confirm scopes & quota before cloning. If your key was created with restricted permissions, this will tell you.
              </p>
              {keyTestResult && keyTestResult.ok && (
                <div className="mt-2 p-2 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-800" data-testid="key-test-success">
                  ✓ Key valid · <strong>{keyTestResult.voices_count}</strong> voice{keyTestResult.voices_count === 1 ? "" : "s"} ({keyTestResult.cloned_count} cloned)
                  {keyTestResult.tier && <> · tier: <strong>{keyTestResult.tier}</strong></>}
                  {keyTestResult.character_limit > 0 && (
                    <> · {keyTestResult.character_count?.toLocaleString()} / {keyTestResult.character_limit?.toLocaleString()} chars used</>
                  )}
                </div>
              )}
              {keyTestResult && !keyTestResult.ok && (
                <div className="mt-2 p-2 rounded bg-red-50 border border-red-200 text-xs text-red-800" data-testid="key-test-fail">
                  {keyTestResult.code === "permissions" ? "🔑 " : "⚠ "}
                  {keyTestResult.message}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={testKey}
              disabled={testingKey}
              className="text-xs px-3 py-2 rounded-lg border-2 border-[#E91E8C] text-[#E91E8C] hover:bg-[#E91E8C]/10 font-semibold disabled:opacity-50 flex-shrink-0 flex items-center gap-1.5"
              data-testid="test-elevenlabs-btn"
            >
              {testingKey ? <><CircleNotch size={12} className="animate-spin" /> Testing…</> : <>Test Key</>}
            </button>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Record / Upload / Clone */}
        <div className="space-y-5">
          {/* Provider toggle */}
          <div className="card p-4 space-y-3" data-testid="clone-provider-card">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">1. Provider</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setProvider("elevenlabs")}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  provider === "elevenlabs"
                    ? "border-[#E91E8C] bg-[#E91E8C]/5"
                    : "border-zinc-200 hover:border-zinc-300"
                }`}
                data-testid="clone-provider-elevenlabs"
              >
                <p className="text-xs font-bold text-zinc-900">🎤 ElevenLabs</p>
                <p className="text-[10px] text-zinc-500 mt-0.5">Instant Voice Clone · ~1 min audio · BYOK (free tier OK)</p>
                {!elevenlabsAvailable && (
                  <p className="text-[9px] font-bold mt-1 text-amber-600" data-testid="clone-elevenlabs-status">⚠ {({
                    present_unvalidated: "KEY STORED — NOT VALIDATED",
                    invalid_permissions: "KEY PERMISSIONS REQUIRED",
                    quota_or_plan_issue: "QUOTA / PLAN ISSUE",
                    missing: "KEY MISSING",
                  })[elevenlabsStatus] || "KEY MISSING"}</p>
                )}
              </button>
              <button
                type="button"
                onClick={() => setProvider("minimax")}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  provider === "minimax"
                    ? "border-[#E91E8C] bg-[#E91E8C]/5"
                    : "border-zinc-200 hover:border-zinc-300"
                }`}
                data-testid="clone-provider-minimax"
              >
                <p className="text-xs font-bold text-zinc-900">⚡ MiniMax</p>
                <p className="text-[10px] text-zinc-500 mt-0.5">7 emotion presets · uses your muapi key (no new signup)</p>
                {!muapiAvailable && <p className="text-[9px] text-amber-600 font-bold mt-1">⚠ MUAPI KEY MISSING</p>}
              </button>
            </div>
          </div>

          {/* Recorder */}
          <div className="card p-4 space-y-4" data-testid="clone-recorder-card">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">2. Record your voice</label>
              <span className={`text-xs font-bold tabular-nums ${durationColor}`} data-testid="record-timer">
                {formatTime(elapsed)} / {formatTime(RECORD_MAX_SEC)}
              </span>
            </div>

            {/* Pulse + button */}
            <div className="flex flex-col items-center gap-3 py-4">
              <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                disabled={cloning}
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all shadow-lg ${
                  recording
                    ? "bg-red-500 hover:bg-red-600 animate-pulse"
                    : "bg-[#E91E8C] hover:bg-[#C8186F]"
                } text-white disabled:opacity-50`}
                data-testid={recording ? "stop-record-btn" : "start-record-btn"}
              >
                {recording ? <Stop size={32} weight="fill" /> : <Microphone size={32} weight="fill" />}
              </button>
              <p className="text-xs text-zinc-500 text-center">
                {recording
                  ? `Recording — ${elapsed < RECORD_MIN_SEC ? `keep going, ${RECORD_MIN_SEC - elapsed}s to minimum` : "you can stop anytime"}`
                  : recordedBlob
                    ? `Recorded ${formatTime(elapsed)}. Click mic to re-record.`
                    : `Tap to start. Aim for ~${RECOMMEND_SEC}s of natural, clear speech.`}
              </p>
            </div>

            {/* Progress bar threshold */}
            <div className="space-y-1">
              <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden relative">
                <div
                  className={`h-full transition-all duration-500 ${
                    elapsed < RECORD_MIN_SEC ? "bg-red-400" : elapsed <= RECORD_MAX_SEC ? "bg-emerald-500" : "bg-amber-500"
                  }`}
                  style={{ width: `${Math.min(100, (elapsed / RECORD_MAX_SEC) * 100)}%` }}
                />
                {/* Minimum threshold marker */}
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-zinc-400"
                  style={{ left: `${(RECORD_MIN_SEC / RECORD_MAX_SEC) * 100}%` }}
                  title={`${RECORD_MIN_SEC}s minimum`}
                />
              </div>
              <div className="flex justify-between text-[10px] text-zinc-400">
                <span>0s</span>
                <span>{RECORD_MIN_SEC}s min</span>
                <span>{RECORD_MAX_SEC}s max</span>
              </div>
            </div>

            {/* Playback */}
            {recordedUrl && (
              <div className="flex items-center gap-2">
                <audio controls src={recordedUrl} className="flex-1 h-8" data-testid="recorded-playback" />
                <button
                  type="button"
                  onClick={discardRecording}
                  className="text-xs text-red-500 px-2 py-1 hover:bg-red-50 rounded"
                  data-testid="discard-recording-btn"
                >
                  Discard
                </button>
              </div>
            )}

            {/* Upload alternative */}
            <div className="pt-3 border-t border-zinc-100">
              <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 block mb-2">Or upload an audio file</label>
              <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-zinc-200 hover:border-[#E91E8C] hover:bg-[#E91E8C]/5 rounded-lg cursor-pointer transition-all">
                <input type="file" accept="audio/*" onChange={handleUpload} className="hidden" data-testid="clone-upload-input" />
                <Upload size={18} className="text-zinc-400" />
                <span className="text-sm text-zinc-600 font-medium">
                  {uploadedFile ? uploadedFile.name : "Drop an mp3/wav (60s+)"}
                </span>
              </label>
              {uploadedUrl && (
                <audio controls src={uploadedUrl} className="w-full h-8 mt-2" data-testid="uploaded-playback" />
              )}
            </div>
          </div>

          {/* Name + clone */}
          <div className="card p-4 space-y-3" data-testid="clone-submit-card">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">3. Name &amp; clone</label>
            <input
              type="text"
              placeholder="e.g. Brittany — gossip wisdom"
              value={voiceName}
              onChange={(e) => setVoiceName(e.target.value)}
              className="input-field text-sm"
              maxLength={60}
              data-testid="clone-voice-name"
            />
            <textarea
              placeholder="Description (optional) — e.g. 'My TikTok talking-head voice'"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="textarea-field text-sm min-h-[60px]"
              maxLength={200}
              data-testid="clone-voice-description"
            />
            {cloneError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="clone-error">{cloneError}</p>
            )}
            {cloneSuccess && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2" data-testid="clone-success">{cloneSuccess}</p>
            )}
            <button
              type="button"
              onClick={submitClone}
              disabled={cloning || !blob || !voiceName.trim() || !durationOk || !providerReady}
              className="btn-primary w-full text-sm py-3 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="clone-submit-btn"
            >
              {cloning ? (
                <><CircleNotch size={18} className="animate-spin" /> Cloning... (~30-60s)</>
              ) : (
                <><Sparkle size={18} weight="fill" /> Clone My Voice {provider === "minimax" ? "(MiniMax)" : "(ElevenLabs)"}</>
              )}
            </button>
            <p className="text-[10px] text-zinc-400 leading-relaxed">
              {provider === "minimax"
                ? "Charged to your muapi credits. The cloned voice automatically appears in every voice picker."
                : "Charged to your ElevenLabs free quota (10k chars/mo). The cloned voice automatically appears in every voice picker across the app."}
            </p>
          </div>
        </div>

        {/* RIGHT: Library + AI Voice Director */}
        <div className="space-y-5">
          {/* AI Voice Director */}
          <div className="card p-4 space-y-4" data-testid="voice-director-card">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                  <Sparkle size={14} weight="fill" className="text-[#E91E8C]" /> AI Voice Director
                </label>
                <p className="text-[11px] text-zinc-400 mt-0.5">Persistent speech directives applied to every render</p>
              </div>
              {savingDirector && <CircleNotch size={14} className="animate-spin text-[#E91E8C]" />}
            </div>

            {/* Speed slider */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-zinc-700">Pace</label>
                <span className="text-xs tabular-nums text-[#E91E8C] font-bold" data-testid="director-speed-value">{director.speed.toFixed(2)}×</span>
              </div>
              <input
                type="range"
                min="0.5" max="2" step="0.05"
                value={director.speed}
                onChange={(e) => setDirector((p) => ({ ...p, speed: Number(e.target.value) }))}
                onMouseUp={(e) => saveDirector({ ...director, speed: Number(e.target.value) })}
                onTouchEnd={(e) => saveDirector({ ...director, speed: Number(e.target.value) })}
                className="w-full accent-[#E91E8C]"
                data-testid="director-speed-slider"
              />
              <div className="flex justify-between text-[9px] text-zinc-400">
                <span>0.5× slow</span>
                <span>1× natural</span>
                <span>2× fast</span>
              </div>
            </div>

            {/* ElevenLabs-only directives */}
            <div className={`space-y-3 pt-3 border-t border-zinc-100 ${elevenlabsAvailable ? "" : "opacity-50 pointer-events-none"}`}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">ElevenLabs cloning controls</p>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-zinc-700" title="How tightly the clone hugs the source voice. Higher = closer to source, lower = more expressive.">Stability</label>
                  <span className="text-xs tabular-nums text-[#E91E8C] font-bold">{Math.round(director.stability * 100)}%</span>
                </div>
                <input
                  type="range" min="0" max="1" step="0.05"
                  value={director.stability}
                  onChange={(e) => setDirector((p) => ({ ...p, stability: Number(e.target.value) }))}
                  onMouseUp={(e) => saveDirector({ ...director, stability: Number(e.target.value) })}
                  onTouchEnd={(e) => saveDirector({ ...director, stability: Number(e.target.value) })}
                  className="w-full accent-[#E91E8C]"
                  data-testid="director-stability-slider"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-zinc-700" title="Loyalty to your unique voice fingerprint.">Similarity</label>
                  <span className="text-xs tabular-nums text-[#E91E8C] font-bold">{Math.round(director.similarity_boost * 100)}%</span>
                </div>
                <input
                  type="range" min="0" max="1" step="0.05"
                  value={director.similarity_boost}
                  onChange={(e) => setDirector((p) => ({ ...p, similarity_boost: Number(e.target.value) }))}
                  onMouseUp={(e) => saveDirector({ ...director, similarity_boost: Number(e.target.value) })}
                  onTouchEnd={(e) => saveDirector({ ...director, similarity_boost: Number(e.target.value) })}
                  className="w-full accent-[#E91E8C]"
                  data-testid="director-similarity-slider"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-zinc-700" title="Stylistic exaggeration for ElevenLabs v2 voices.">Style exaggeration</label>
                  <span className="text-xs tabular-nums text-[#E91E8C] font-bold">{Math.round(director.style * 100)}%</span>
                </div>
                <input
                  type="range" min="0" max="1" step="0.05"
                  value={director.style}
                  onChange={(e) => setDirector((p) => ({ ...p, style: Number(e.target.value) }))}
                  onMouseUp={(e) => saveDirector({ ...director, style: Number(e.target.value) })}
                  onTouchEnd={(e) => saveDirector({ ...director, style: Number(e.target.value) })}
                  className="w-full accent-[#E91E8C]"
                  data-testid="director-style-slider"
                />
              </div>
            </div>
          </div>

          {/* Library */}
          <div className="card p-4 space-y-3" data-testid="cloned-voices-library">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Your cloned voices ({clonedVoices.length})</label>
            </div>
            {clonedVoices.length === 0 ? (
              <p className="text-xs text-zinc-400 italic">No cloned voices yet. Record a sample on the left to get started.</p>
            ) : (
              <div className="space-y-2">
                {clonedVoices.map((v) => (
                  <div key={v.voice_id} className={`p-3 rounded-lg border ${v.is_default ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200"}`} data-testid={`cloned-voice-${v.voice_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-semibold text-zinc-900 truncate">{v.name}</p>
                          {v.is_default && (
                            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#E91E8C] text-white" data-testid={`default-badge-${v.voice_id}`}>Default</span>
                          )}
                        </div>
                        {v.description && <p className="text-[11px] text-zinc-500 truncate">{v.description}</p>}
                        <p className="text-[9px] text-zinc-400 mt-0.5">{new Date(v.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => previewCloned(v)}
                          disabled={previewingId === v.voice_id}
                          className="p-1.5 text-zinc-500 hover:text-[#E91E8C] rounded hover:bg-zinc-50"
                          title="Preview"
                          data-testid={`preview-cloned-${v.voice_id}`}
                        >
                          {previewingId === v.voice_id ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                        </button>
                        {!v.is_default && (
                          <button
                            type="button"
                            onClick={() => setAsDefault(v.voice_id)}
                            className="p-1.5 text-zinc-500 hover:text-amber-500 rounded hover:bg-zinc-50"
                            title="Set as default"
                            data-testid={`set-default-${v.voice_id}`}
                          >
                            <Star size={14} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteCloned(v.voice_id)}
                          disabled={deletingId === v.voice_id}
                          className="p-1.5 text-zinc-400 hover:text-red-500 rounded hover:bg-red-50"
                          title="Delete"
                          data-testid={`delete-cloned-${v.voice_id}`}
                        >
                          {deletingId === v.voice_id ? <CircleNotch size={14} className="animate-spin" /> : <Trash size={14} />}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-zinc-400 italic pt-2 border-t border-zinc-100">
              <Check size={10} className="inline" /> Cloned voices auto-surface in Script Studio · Auto Create · Bulk Create · Series Studio.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloneMyVoice;
